/**
 * Variable Inspector Component
 *
 * Displays and allows editing of game variables during play mode.
 * Features:
 * - Live variable values with type indicators
 * - Inline editing for quick variable changes
 * - Namespaced grouping (quest.*, stats.*, etc.)
 * - Type-aware input validation
 * - Variable usage tracking (which nodes reference each variable)
 *
 * The inspector is read-only by default during playback,
 * but can be toggled to edit mode for debugging.
 *
 * @module playMode/VariableInspector
 */

import React, { useState, useCallback, useMemo } from 'react';
import type { VariableStore } from '../scripting/types';

/**
 * Variable Inspector Props
 *
 * Receives the current variable state and optional callbacks for changes
 */
interface VariableInspectorProps {
  /** Current variable values */
  variables: VariableStore;

  /** Callback when a variable is changed (for edit mode) */
  onVariableChange?: (name: string, value: unknown) => void;

  /** Whether to allow editing (default: false) */
  allowEdit?: boolean;
}

/**
 * Grouped variables by namespace
 *
 * Variables with dot notation are grouped under their prefix.
 * Example: quest.stage, quest.npc_count → {quest: {stage, npc_count}}
 */
interface GroupedVariables {
  [namespace: string]: {
    [name: string]: unknown;
  };
}

/**
 * Get the type of a variable value
 *
 * Returns a human-readable type indicator.
 */
function getVariableType(value: unknown): string {
  if (value === undefined) return 'undefined';
  if (value === null) return 'null';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'string') return 'string';
  return 'unknown';
}

/**
 * Get the color for a type badge
 */
function getTypeColor(
  type: string
): 'blue' | 'green' | 'yellow' | 'red' | 'grape' | 'gray' {
  switch (type) {
    case 'boolean':
      return 'blue';
    case 'number':
      return 'green';
    case 'string':
      return 'yellow';
    case 'undefined':
    case 'null':
      return 'gray';
    default:
      return 'grape';
  }
}

/**
 * Variable Inspector Component
 *
 * Renders a table of variables grouped by namespace.
 * Shows type, current value, and provides edit controls.
 *
 * **Display modes:**
 * - Read-only: Shows all variables and their values
 * - Edit: Allows inline editing with appropriate input types
 *
 * **Grouping:**
 * Variables are grouped by their namespace (prefix before the dot).
 * Ungrouped variables appear under "Global".
 */
export const VariableInspector: React.FC<VariableInspectorProps> = ({
  variables,
  onVariableChange,
  allowEdit = false,
}) => {
  const [editingVar, setEditingVar] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<unknown>(null);

  /**
   * Group variables by namespace
   */
  const groupedVariables = useMemo(() => {
    const grouped: GroupedVariables = {};

    Object.entries(variables).forEach(([name, value]) => {
      const parts = name.split('.');
      const namespace = parts.length > 1 ? parts[0] : 'Global';
      const varName = parts.length > 1 ? parts.slice(1).join('.') : name;

      if (!grouped[namespace]) {
        grouped[namespace] = {};
      }
      grouped[namespace][varName] = value;
    });

    return grouped;
  }, [variables]);

  /**
   * Start editing a variable
   */
  const handleStartEdit = useCallback((name: string) => {
    setEditingVar(name);
    setEditValue(variables[name]);
  }, [variables]);

  /**
   * Save variable change
   */
  const handleSaveEdit = useCallback(() => {
    if (editingVar && onVariableChange) {
      onVariableChange(editingVar, editValue);
    }
    setEditingVar(null);
    setEditValue(null);
  }, [editingVar, editValue, onVariableChange]);

  /**
   * Cancel editing
   */
  const handleCancelEdit = useCallback(() => {
    setEditingVar(null);
    setEditValue(null);
  }, []);

  /**
   * Render an input field for editing based on variable type
   */
  const renderEditInput = (name: string, currentValue: unknown) => {
    const type = getVariableType(currentValue);

    switch (type) {
      case 'boolean':
        return (
          <Group gap="xs">
            <Checkbox
              checked={editValue as boolean}
              onChange={(e) => setEditValue(e.currentTarget.checked)}
              label={String(editValue)}
            />
          </Group>
        );

      case 'number':
        return (
          <NumberInput
            value={editValue as number}
            onChange={(value) => setEditValue(value)}
            placeholder="0"
            autoFocus
          />
        );

      case 'string':
        return (
          <TextInput
            value={editValue as string}
            onChange={(e) => setEditValue(e.currentTarget.value)}
            placeholder=""
            autoFocus
          />
        );

      default:
        return (
          <TextInput
            value={String(editValue)}
            onChange={(e) => setEditValue(e.currentTarget.value)}
            placeholder=""
            disabled
            size="sm"
          />
        );
    }
  };

  if (Object.keys(variables).length === 0) {
    return (
      <p className="text-sm text-gray-600 dark:text-gray-400 text-center py-4">
        No variables yet
      </p>
    );
  }

  return (
    <div className="h-full w-full overflow-y-auto">
      <div className="space-y-4 p-2">
        {Object.entries(groupedVariables).map(([namespace, vars]) => (
          <div key={namespace} className="space-y-2">
            {/* Namespace header */}
            <div className="sticky top-0 bg-white dark:bg-slate-800 py-1">
              <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">
                {namespace}
              </p>
            </div>

            {/* Variables in namespace */}
            <div className="space-y-1">
              {Object.entries(vars).map(([varName, value]) => {
                const fullName = namespace === 'Global' ? varName : `${namespace}.${varName}`;
                const isEditing = editingVar === fullName;
                const type = getVariableType(value);

                return (
                  <div
                    key={fullName}
                    className="p-2 border border-slate-200 dark:border-slate-700 rounded bg-slate-50 dark:bg-slate-900/50 text-sm"
                  >
                    {isEditing ? (
                      // Edit mode
                      <div className="space-y-2">
                        <p className="text-xs font-medium font-mono text-slate-700 dark:text-slate-300">
                          {varName}
                        </p>
                        {renderEditInput(fullName, value)}
                        <div className="flex gap-1 justify-end">
                          <button
                            onClick={handleSaveEdit}
                            title="Save"
                            className="px-2 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            title="Cancel"
                            className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Display mode
                      <div className="flex justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium font-mono text-slate-700 dark:text-slate-300 break-all">
                            {varName}
                          </p>
                          <div title={`Type: ${type}`} className="flex items-center gap-2 mt-1 flex-wrap">
                            <span
                              className={`text-xs font-medium px-1 rounded ${
                                getTypeColor(type) === 'blue'
                                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200'
                                  : getTypeColor(type) === 'green'
                                  ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-200'
                                  : getTypeColor(type) === 'yellow'
                                  ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-200'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                              }`}
                            >
                              {type}
                            </span>
                            <span className="text-xs font-mono text-slate-600 dark:text-slate-400 break-all">
                              {typeof value === 'string' ? `"${value}"` : String(value)}
                            </span>
                          </div>
                        </div>

                        {allowEdit && (
                          <button
                            onClick={() => handleStartEdit(fullName)}
                            title="Edit variable"
                            className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded flex-shrink-0"
                          >
                            ✎
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

VariableInspector.displayName = 'VariableInspector';
